import React, { useContext, useEffect, useState } from "react"
import { CoolButton } from "../CoolButton"
import { Banner, Spinner } from "@shopify/polaris"
import {
	useCheckScopes,
	useCreateScriptTag,
} from "../../hooks/useProtectHooks.js"
import { GlobalStateContext } from "../providers/GlobalStateProvider.jsx"

export const ScriptTagGuide = ({ onScriptTagCreate }) => {
	const { state } = useContext(GlobalStateContext)
	const createScriptTag = useCreateScriptTag()
	const checkScopes = useCheckScopes()

	const [step, setStep] = useState(1)
	const [hasNeccessaryScopes, setHasNeccessaryScopes] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	const openEditPermissions = () => {
		const scopesUrl = `https://admin.shopify.com/store/${state.shop.split(".myshopify.com")?.[0]}/settings/apps/development/${state.appId}/configuration/admin_api_integration`

		window.open(scopesUrl, "_blank")
	}

	const _checkScopes = async () => {
		setLoading(true)
		const { hasNeccessaryScopes } = await checkScopes(["write_script_tags"])
		setLoading(false)

		if (!hasNeccessaryScopes) {
			setHasNeccessaryScopes(false)
		} else {
			setHasNeccessaryScopes(true)
			setStep(2)
			window.removeEventListener("focus", _checkScopes)
		}
	}

	const _createScriptTag = async () => {
		setLoading(true)
		const { scriptTagId, error } = await createScriptTag()
		setLoading(false)

		if (error) {
			setError(error)
			return
		}
		if (scriptTagId) {
			onScriptTagCreate()
		}
	}

	useEffect(() => {
		if (!state.appId) {
			const url = new URL(window.location.href)
			if (!url.searchParams.get("reload")) {
				url.searchParams.append("reload", "true")
				window.location = url
			}
		}

		_checkScopes()

		// Add event listener on tab change
		window.addEventListener("focus", _checkScopes)

		return () => {
			window.removeEventListener("focus", _checkScopes)
		}
	}, [])

	return (
		<div
			style={{
				maxWidth: "500px",
				marginLeft: "auto",
				marginRight: "auto",
			}}
		>
			<h1
				style={{
					fontSize: "2rem",
					fontWeight: "800",
					marginBottom: "1rem",
				}}
			>
				Get Started
			</h1>

			<div
				style={{
					justifyContent: "center",
					gap: "0.5rem",
					background: "#222",
					borderRadius: "1rem",
					padding: "1rem",
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
					}}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						style={{
							height: "1.3rem",
							width: "1.3rem",
							color: "#fff",
						}}
						viewBox="0 0 512 512"
					>
						<path
							fill="currentColor"
							d="M256 0c4.6 0 9.2 1 13.4 2.9l188.3 79.9c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0m0 66.8v378C394 378 431.1 230.1 432 141.4z"
						></path>
					</svg>
					<p
						style={{
							fontSize: "1.25rem",
							fontWeight: "600",
							color: "#fff",
						}}
					>
						Kopy Protect - Installation
					</p>
				</div>

				<div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
					<div
						style={{
							display: "flex",
							gap: "0.3rem",
							alignItems: "start",
							marginBottom: "1rem",
							color: step === 2 ? "#777" : "#fff",
						}}
					>
						<div style={{ width: "1.5rem", height: "1.5rem" }}>
							{step === 1 ? (
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="1.5rem"
									height="1.5rem"
									viewBox="0 0 24 24"
								>
									<path
										fill="none"
										stroke="currentColor"
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M4 7.2v9.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874c.427.218.987.218 2.105.218h9.606c1.118 0 1.677 0 2.104-.218c.377-.192.683-.498.875-.874c.218-.428.218-.986.218-2.104V7.197c0-1.118 0-1.678-.218-2.105a2.001 2.001 0 0 0-.875-.874C18.48 4 17.92 4 16.8 4H7.2c-1.12 0-1.68 0-2.108.218a1.999 1.999 0 0 0-.874.874C4 5.52 4 6.08 4 7.2"
									></path>
								</svg>
							) : step === 2 ? (
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="1.5rem"
									height="1.5rem"
									viewBox="0 0 24 24"
								>
									<path
										fill="none"
										stroke="currentColor"
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="m8 12l3 3l5-6M4 16.8V7.2c0-1.12 0-1.68.218-2.108c.192-.377.497-.682.874-.874C5.52 4 6.08 4 7.2 4h9.6c1.12 0 1.68 0 2.107.218c.377.192.683.497.875.874c.218.427.218.987.218 2.105v9.607c0 1.118 0 1.677-.218 2.104a2.002 2.002 0 0 1-.875.874c-.427.218-.986.218-2.104.218H7.197c-1.118 0-1.678 0-2.105-.218a2 2 0 0 1-.874-.874C4 18.48 4 17.92 4 16.8"
									></path>
								</svg>
							) : (
								""
							)}
						</div>
						<p style={{ fontSize: "1rem" }}>
							Step 1. Enable the{" "}
							<b style={{ textDecoration: "underline dotted" }}>
								write_script_tags
							</b>{" "}
							and{" "}
							<b style={{ textDecoration: "underline dotted" }}>
								read_script_tags
							</b>{" "}
							permission.
						</p>
					</div>

					<div
						style={{
							display: "flex",
							gap: "0.3rem",
							alignItems: "start",
							color: step === 1 ? "#777" : "#fff",
						}}
					>
						<div style={{ width: "1.5rem", height: "1.5rem" }}>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="1.5rem"
								height="1.5rem"
								viewBox="0 0 24 24"
							>
								<path
									fill="none"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M4 7.2v9.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874c.427.218.987.218 2.105.218h9.606c1.118 0 1.677 0 2.104-.218c.377-.192.683-.498.875-.874c.218-.428.218-.986.218-2.104V7.197c0-1.118 0-1.678-.218-2.105a2.001 2.001 0 0 0-.875-.874C18.48 4 17.92 4 16.8 4H7.2c-1.12 0-1.68 0-2.108.218a1.999 1.999 0 0 0-.874.874C4 5.52 4 6.08 4 7.2"
								></path>
							</svg>
						</div>
						<p style={{ fontSize: "1rem" }}>
							Step 2. Install the Kopy Protect script in your
							store.
						</p>
					</div>
				</div>

				{error ? (
					<div style={{ marginBottom: "1rem" }}>
						<Banner
							title="Error ocurred while installing script"
							status="critical"
						>
							{error}
						</Banner>
					</div>
				) : null}

				<CoolButton
					onClick={
						loading
							? null
							: step === 1
								? openEditPermissions
								: step === 2
									? _createScriptTag
									: null
					}
				>
					{loading ? (
						<Spinner size="small" />
					) : step === 1 ? (
						<span>Enable Permissions</span>
					) : step === 2 ? (
						<span>Install</span>
					) : (
						""
					)}
				</CoolButton>
			</div>
		</div>
	)
}
